import { useCallback, useEffect, useState } from 'react';
import { Download } from '../../icons';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
import {
  listCertificates, issueCertificate, rejectCertificate, getCertificateDocument,
  type CertificateDto, type CertificateDocumentDto, type CertStatus,
} from '../../api/hr-certificate';
import { CertificateDoc } from './CertificateDoc';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';

/**
 * 증명서 발급 관리 — 담당자 화면 (시안 A7, migration 129).
 *
 * ★★ **대기가 먼저 온다.** 담당자가 이 화면에 오는 이유는 «누가 기다리나» 하나다 — 처음 여는 판을
 *    대기로 두고, 전체는 눌러서 본다.
 * ★ 재직증명서는 여기 잘 오지 않는다 — 직원이 그 자리에서 받아 가기 때문이다(서버가 즉시 발급한다).
 *   여기 쌓이는 것은 대개 **경력증명서**다.
 * ★ 이미 낸 것은 다시 내지도 반려하지도 못한다(서버가 409). 화면도 그 자리에 단추를 두지 않는다.
 */
const TABS: Array<{ key: CertStatus | 'ALL'; label: string }> = [
  { key: 'REQUESTED', label: '대기' },
  { key: 'ISSUED', label: '발급 완료' },
  { key: 'REJECTED', label: '반려' },
  { key: 'ALL', label: '전체' },
];

const STATUS_LABEL: Record<string, string> = { ISSUED: '발급', REQUESTED: '대기', REJECTED: '반려' };
const STATUS_TONE: Record<string, string> = { ISSUED: 'is-ok', REQUESTED: 'is-wait', REJECTED: 'is-no' };

export function CertificateAdminPage() {
  const [tab, setTab] = useState<CertStatus | 'ALL'>('REQUESTED');
  const [rows, setRows] = useState<CertificateDto[]>([]);
  const [pending, setPending] = useState(0);
  const [doc, setDoc] = useState<CertificateDocumentDto | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');

  const load = useCallback(async (which: CertStatus | 'ALL') => {
    const res = await listCertificates(which === 'ALL' ? undefined : which);
    setRows(res.certificates);
    setPending(res.pending);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await load(tab);
        if (alive) setState('ok');
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, [tab, load]);

  const act = async (id: string, run: () => Promise<unknown>) => {
    setBusyId(id);
    setError(null);
    try {
      await run();
      await load(tab);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? '처리하지 못했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  const reject = (id: string) => {
    const reason = window.prompt('반려 사유를 적어 주세요 — 신청한 사람에게 그대로 보입니다.');
    if (!reason || !reason.trim()) return;
    void act(id, () => rejectCertificate(id, reason.trim()));
  };

  return (
    <div className="hrpage">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/certificates')}
        scope={scopeFor('/erp/hr/certificates')}
        title="증명서 발급"
        description="재직증명서는 직원이 그 자리에서 받아 갑니다 — 여기 쌓이는 것은 확인이 필요한 것입니다"
      />

      {state === 'folded' && (
        <div className="hrpage__note">증명서 신청함을 불러오지 못했습니다 — 서버가 아직 준비되지 않았을 수 있습니다.</div>
      )}

      {state !== 'folded' && (
        <>
          <div className="hrcert__tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`hrcert__tab${tab === t.key ? ' is-on' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {t.key === 'REQUESTED' && pending > 0 ? <span className="hrcert__count">{pending}</span> : null}
              </button>
            ))}
          </div>

          {error && <p className="hrcert__err" role="alert">{error}</p>}

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              신청함
              <span className="hrpage__panels">{rows.length}건</span>
            </h2>
            {rows.length === 0 ? (
              <p className="hrpage__empty">
                {tab === 'REQUESTED' ? '기다리는 신청이 없습니다.' : '해당하는 신청이 없습니다.'}
              </p>
            ) : (
              <table className="hrpage__tbl">
                <thead>
                  <tr>
                    <th>신청일</th><th>신청자</th><th>종류</th><th>용도 · 제출처</th>
                    <th className="hrpage__num">부수</th><th className="hrpage__act">처리</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.createdAt.slice(0, 10)}</td>
                      <td><b>{r.userName ?? '—'}</b></td>
                      <td>
                        {r.kindLabel}
                        {r.docNo ? <span className="hrpage__dim"> · {r.docNo}</span> : null}
                      </td>
                      <td>
                        {[r.purpose, r.recipient].filter(Boolean).join(' · ') || <span className="hrpage__dim">—</span>}
                        {r.rejectReason ? <span className="hrpage__dim"> · {r.rejectReason}</span> : null}
                      </td>
                      <td className="hrpage__num">{r.copies}</td>
                      <td className="hrpage__act">
                        {r.status === 'REQUESTED' ? (
                          <>
                            <Button variant="primary" size="compact" disabled={busyId === r.id} onClick={() => act(r.id, () => issueCertificate(r.id))}
                            >발급</Button>
                            <Button variant="secondary" size="sm" disabled={busyId === r.id} onClick={() => reject(r.id)}
                            >반려</Button>
                          </>
                        ) : (
                          <>
                            <span className={`hrpage__pill ${STATUS_TONE[r.status] ?? ''}`}>
                              {STATUS_LABEL[r.status] ?? r.status}
                            </span>
                            {r.status === 'ISSUED' ? (
                              <Button variant="secondary" size="sm" onClick={() => void getCertificateDocument(r.id).then(setDoc).catch(() => setError('증명서를 불러오지 못했습니다.'))}
                              >보기</Button>
                            ) : null}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {doc && (
            <section className="hrpage__panel hrcert__docwrap">
              <h2 className="hrpage__panelt">
                {doc.name} · {doc.kindLabel}
                <span className="hrpage__panels">{doc.docNo}</span>
                <Button variant="secondary" size="compact" className="hrcert__printbtn"
                  onClick={() => window.print()}>
                  <Download size={14} aria-hidden /> 인쇄 · PDF
                </Button>
              </h2>
              <div className="hrcert__docbody">
                <CertificateDoc doc={doc} />
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
